/**
 * Canonical money/number/date/date-time display formatters — the single
 * place every currency, plain-number, and date/date-time value in the app
 * gets turned into a string, replacing three previously-incompatible
 * families (a manual `'$' + n.toLocaleString(...)` shape, ad hoc
 * `new Intl.NumberFormat(...)` instances re-created per call, and a third
 * `n.toLocaleString('en-US', {style:'currency',...})` variant) plus a dozen
 * one-off `toLocaleDateString()`/`toLocaleString()` call sites.
 *
 * Locale is an explicit, opt-in parameter, not a hidden default:
 *   - Browser call sites omit `locale` entirely, so `Intl` falls back to the
 *     viewer's own runtime/browser locale — a deliberate behavior change
 *     from this app's previous universal `'en-US'` hardcode.
 *   - The server-side export path (`resolved-view-export.ts`, via
 *     `formatColumnValue`/`formatAggregateResult`/`formatFooterAggregateValue`
 *     in `columnar-aggregation.ts`) MUST pass `EXPORT_LOCALE` explicitly.
 *     `formatColumnValue` is reachable from that path through the
 *     footer-aggregate calculation — without the explicit pin, an exported
 *     report's totals row could silently stop matching what the live
 *     browser grid showed the moment a Lambda cold-starts under a
 *     non-`en-US` ICU default (FE-13's byte-identical-export guarantee).
 *
 * Deliberately NOT here: `formatDate(epochDays)`/`formatDecimal`/
 * `formatTimestamp` in `./format-decimal.ts` — those are pure `bigint`/`Date`
 * math with no `Intl` involved on purpose (their locale-independence is what
 * makes the exact-decimal export path deterministic by construction, not by
 * discipline). Named `formatDateValue` here, not `formatDate`, specifically
 * to avoid colliding with that unrelated, intentionally-narrower function.
 *
 * The one exception is `parseDecimal`/`rescaleDecimal`, imported below: a
 * currency column arrives here as the exact rendered string those functions
 * understand, so recognising it is part of formatting money correctly rather
 * than a locale concern (IC-499).
 */

import { parseDecimal, rescaleDecimal } from './format-decimal';

/** BCP-47 tag. Omit (`undefined`) in browser code — `Intl` falls back to the
 *  runtime default, i.e. the viewer's own browser locale. Server/export code
 *  MUST pass {@link EXPORT_LOCALE} explicitly. */
export interface LocaleOption {
  locale?: string;
}

/** The locale the server-side export path pins so exported bytes are
 *  identical regardless of the Lambda runtime's own ICU default (FE-13). */
export const EXPORT_LOCALE = 'en-US';

const DEFAULT_NULL_DISPLAY = '—';

export interface MoneyFormatOptions extends LocaleOption {
  /** ISO 4217 currency code. Default `'USD'`. */
  currency?: string;
  /** Rendered when `value` is `null`/`undefined`. Default `'—'`. Callers
   *  that need an empty grid/export cell instead pass `''` explicitly. */
  nullDisplay?: string;
  /** Overrides the currency's default decimal precision (usually 2) — e.g.
   *  FE-16's `C0` report-column format wants a whole-dollar amount. Omit
   *  for the pre-existing locale-default behavior. */
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
}

export interface NumberFormatOptions extends LocaleOption {
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** Default `true` (existing behavior). `false` for FE-16's `g`/`G` report
   *  format, which — unlike `N2`/`C2` — has no forced thousands separator. */
  useGrouping?: boolean;
  /** Rendered when `value` is `null`/`undefined`. Default `'—'`. */
  nullDisplay?: string;
}

export interface DateFormatOptions extends LocaleOption {
  /** Rendered when `value` is `null`/`undefined`. Default `'—'`. */
  nullDisplay?: string;
  /** Rendered when `value` parses to an invalid date. Default: whatever
   *  `nullDisplay` resolves to. */
  invalidDisplay?: string;
  /** Overrides the default date (or date+time) component shape. */
  dateTimeFormat?: Intl.DateTimeFormatOptions;
}

const numberFormatCache = new Map<string, Intl.NumberFormat>();
const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>();

function moneyFormatter(
  locale: string | undefined,
  currency: string,
  minimumFractionDigits?: number,
  maximumFractionDigits?: number,
): Intl.NumberFormat {
  const key = `${locale ?? ''}|money|${currency}|${minimumFractionDigits ?? ''}|${maximumFractionDigits ?? ''}`;
  let formatter = numberFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      ...(minimumFractionDigits != null ? { minimumFractionDigits } : {}),
      ...(maximumFractionDigits != null ? { maximumFractionDigits } : {}),
    });
    numberFormatCache.set(key, formatter);
  }
  return formatter;
}

function plainNumberFormatter(
  locale: string | undefined,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
  useGrouping = true,
): Intl.NumberFormat {
  const key = `${locale ?? ''}|number|${minimumFractionDigits}|${maximumFractionDigits}|${useGrouping}`;
  let formatter = numberFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { minimumFractionDigits, maximumFractionDigits, useGrouping });
    numberFormatCache.set(key, formatter);
  }
  return formatter;
}

const DEFAULT_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
};

const DEFAULT_DATETIME_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
};

function dateTimeFormatter(locale: string | undefined, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale ?? ''}|${JSON.stringify(options)}`;
  let formatter = dateTimeFormatCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options);
    dateTimeFormatCache.set(key, formatter);
  }
  return formatter;
}

/** Formats a money amount as e.g. `"$1,234.56"`. Replaces every manual
 *  `'$' + n.toLocaleString(...)`, ad hoc `new Intl.NumberFormat(...)`, and
 *  `n.toLocaleString('en-US', {style:'currency',...})` call site.
 *
 *  Accepts a **rendered exact-decimal string** (`"1,000.0000"`) as well as a
 *  `number`, because that string - not a number - is what the Parquet result
 *  substrate actually puts in a currency column (`formatDecimal()`, see
 *  `parquet-result-set-source.ts`). Such a value is re-scaled on the `bigint`,
 *  never through `Number`, so no precision is lost on the way to the screen.
 *  Anything non-finite or unparseable renders `nullDisplay` - IC-499: this
 *  function used to hand `"1,000.0000"` straight to `Intl`, whose `Number()`
 *  coercion chokes on the comma and painted a literal `$NaN` onto a cash
 *  approval screen. */
export function formatMoney(value: number | string | null | undefined, opts?: MoneyFormatOptions): string {
  const fallback = opts?.nullDisplay ?? DEFAULT_NULL_DISPLAY;
  if (value == null) return fallback;
  // Re-scale to whatever precision the OUTPUT actually wants (2 decimals
  // unless the caller overrides it, e.g. FE-16's C0), never to a fixed 2 —
  // rescaling is still exact bigint math, so a C0 report column rounds the
  // same cent-accurate way formatMoney's default 2-decimal path always has.
  const outputScale = opts?.maximumFractionDigits ?? 2;
  if (typeof value === 'string') {
    const parsed = parseDecimal(value);
    if (parsed === null) return fallback;
    // Re-scale exactly, then let Intl attach the currency symbol/grouping.
    // `rescaleDecimal` already rounded, so this cannot lose a cent.
    const rescaled = rescaleDecimal(parsed.unscaled, parsed.scale, outputScale).replaceAll(',', '');
    return moneyFormatter(
      opts?.locale,
      opts?.currency ?? 'USD',
      opts?.minimumFractionDigits,
      opts?.maximumFractionDigits,
    ).format(Number(rescaled));
  }
  if (!Number.isFinite(value)) return fallback;
  return moneyFormatter(
    opts?.locale,
    opts?.currency ?? 'USD',
    opts?.minimumFractionDigits,
    opts?.maximumFractionDigits,
  ).format(value);
}

/** Converts a rendered decimal string to a `number` for the plain-number
 *  formatter, or `Number.NaN` when it is not one. Money never takes this
 *  path — {@link formatMoney} re-scales on the `bigint` instead, because a
 *  currency value must not round-trip through a float. */
function decimalTextToNumber(text: string): number {
  if (parseDecimal(text) === null) return Number.NaN;
  return Number(text.replaceAll(',', ''));
}

/** Formats a plain number (not money) as e.g. `"1,234.5"`. Accepts a rendered
 *  exact-decimal string for the same reason {@link formatMoney} does, and
 *  degrades to `nullDisplay` rather than `NaN` on anything unparseable. */
export function formatNumber(value: number | string | null | undefined, opts?: NumberFormatOptions): string {
  const fallback = opts?.nullDisplay ?? DEFAULT_NULL_DISPLAY;
  if (value == null) return fallback;
  const numeric = typeof value === 'string' ? decimalTextToNumber(value) : value;
  if (!Number.isFinite(numeric)) return fallback;
  return plainNumberFormatter(
    opts?.locale,
    opts?.minimumFractionDigits ?? 0,
    opts?.maximumFractionDigits ?? 2,
    opts?.useGrouping ?? true,
  ).format(numeric);
}

function toDate(value: string | number | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

/** Matches a value that encodes a calendar date with no genuine time-of-day
 *  component: a bare `YYYY-MM-DD` string (an `<input type="date">` value,
 *  never round-tripped through a `Date`) or the exact
 *  `YYYY-MM-DDT00:00:00.000Z` shape a SQL `date` column always serializes to
 *  once it crosses a JSON boundary. A genuine instant (`new Date()`,
 *  `doc.createdOn`, a build timestamp) essentially never lands on exact UTC
 *  midnight, so it never matches. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}(T00:00:00(\.\d+)?Z)?$/;

export function isDateOnlyValue(value: string | number | Date): boolean {
  return typeof value === 'string' && DATE_ONLY_PATTERN.test(value);
}

/** Formats a calendar date only (no time-of-day) — replaces every raw
 *  `toLocaleDateString()` call site. NaN-safe: an unparseable `value`
 *  renders `invalidDisplay` (default: `nullDisplay`) instead of the literal
 *  string `"Invalid Date"`.
 *
 *  A date-only value (see {@link isDateOnlyValue}) is read back using ITS
 *  OWN UTC calendar components rather than the viewer's local timezone,
 *  unless the caller already supplied an explicit `timeZone` — this
 *  function's own contract is "a calendar date, not an instant", so its
 *  displayed day must not depend on where the viewer happens to be sitting.
 *  Without this, a SQL `date` column (e.g. Machine Count's `GameDay`) that
 *  serializes to `"2026-09-10T00:00:00.000Z"` renders as `9/9/2026` for any
 *  viewer west of UTC, because `Intl.DateTimeFormat` with no `timeZone`
 *  falls back to the browser's local zone and that UTC-midnight instant is
 *  still the evening of the *previous* day there. A genuine timestamp
 *  (`new Date()`, `doc.createdOn`, a build timestamp) does not match
 *  {@link isDateOnlyValue} and keeps rendering in the viewer's own local
 *  time, which is the correct behavior for those. */
export function formatDateValue(
  value: string | number | Date | null | undefined,
  opts?: DateFormatOptions,
): string {
  if (value == null) return opts?.nullDisplay ?? DEFAULT_NULL_DISPLAY;
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return opts?.invalidDisplay ?? opts?.nullDisplay ?? DEFAULT_NULL_DISPLAY;
  const baseFormat = opts?.dateTimeFormat ?? DEFAULT_DATE_FORMAT;
  const format =
    isDateOnlyValue(value) && !baseFormat.timeZone ? { ...baseFormat, timeZone: 'UTC' } : baseFormat;
  return dateTimeFormatter(opts?.locale, format).format(d);
}

/** Formats a date + time-of-day — replaces every raw
 *  `new Date(x).toLocaleString()` call site. Same NaN-safety as
 *  {@link formatDateValue}. */
export function formatDateTimeValue(
  value: string | number | Date | null | undefined,
  opts?: DateFormatOptions,
): string {
  if (value == null) return opts?.nullDisplay ?? DEFAULT_NULL_DISPLAY;
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return opts?.invalidDisplay ?? opts?.nullDisplay ?? DEFAULT_NULL_DISPLAY;
  return dateTimeFormatter(opts?.locale, opts?.dateTimeFormat ?? DEFAULT_DATETIME_FORMAT).format(d);
}
